import { RotateCw } from 'lucide-react'
import { Francisco } from '@/components/francisco/Francisco'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

/* Loading, failure and "system unavailable" states shared by the screens. */

export function SplashScreen() {
    return (
        <main className="grid min-h-dvh place-items-center p-6" aria-busy="true">
            <div className="flex flex-col items-center gap-3">
                <Francisco color="g" mood="thinking" size={64} follow={false} />
                <span className="text-[13px] text-muted">Carregando o painel…</span>
            </div>
        </main>
    )
}

export function errorMessage(error: unknown): string {
    if (error instanceof ApiError && error.code === 'network') return 'Sem conexão com o painel. Confira a rede.'
    if (error instanceof ApiError && error.code === 'forbidden') return 'Você não tem acesso a esta informação.'
    return 'Sistema indisponível agora'
}

type UnavailableProps = { error: unknown; onRetry?: () => void; className?: string }

/** "Sistema indisponível agora" inside the affected panel; the rest of the screen keeps working. */
export function Unavailable({ error, onRetry, className }: UnavailableProps) {
    return (
        <div role="alert" className={cn('flex flex-wrap items-center gap-x-4 gap-y-2.5 py-1', className)}>
            <Francisco color="g" mood="confused" size={40} follow={false} />
            <div className="flex min-w-0 flex-[1_1_220px] flex-col gap-0.5">
                <span className="text-[14px] font-semibold">{errorMessage(error)}</span>
                <span className="text-[13px] text-muted">Os dados voltam assim que o sistema responder.</span>
            </div>
            {onRetry && (
                <button type="button" onClick={onRetry} className={secondaryButton}>
                    <RotateCw size={14} strokeWidth={2.2} aria-hidden="true" />
                    Tentar de novo
                </button>
            )}
        </div>
    )
}

export function ErrorScreen({ error, onRetry }: { error: unknown; onRetry: () => void }) {
    return (
        <main className="grid min-h-dvh place-items-center p-4">
            <div className="w-full max-w-[440px] rounded-[14px] border border-line bg-panel px-4 py-3.5">
                <Unavailable error={error} onRetry={onRetry} />
            </div>
        </main>
    )
}

/** Panel box used across the screens (compact density: 14px 16px, radius 14). */
export const panelBox = 'min-w-0 rounded-[14px] border border-line bg-panel px-4 py-3.5'
/* Buttons (BRAND.md, classes in main.css): ctaButton = the ONE call to action per screen (laranja, grafite text);
   primaryButton = other confirms (verde, white text); secondaryButton = menta bg + green text;
   quietButton = transparent + green; outlineButton = line border + grafite text.
   Add `small` (32px) for in-row actions, pagers and chips-as-buttons; `iconOnly` for square icon buttons. */
export const ctaButton = 'cf-btn cf-btn--cta'
export const primaryButton = 'cf-btn cf-btn--primary'
export const secondaryButton = 'cf-btn cf-btn--secondary'
export const quietButton = 'cf-btn cf-btn--quiet'
export const outlineButton = 'cf-btn cf-btn--outline'
export const small = 'cf-btn--sm'
export const iconOnly = 'cf-btn--icon'
/** Destructive confirm (Recusar, Encerrar): red family only. */
export const dangerStyle = { background: 'var(--red-bg)', borderColor: 'color-mix(in srgb, var(--red) 45%, transparent)', color: 'var(--red-ink)' }
